/**
 * L5 — Full Screen Headline
 * Large animated headline on a solid/gradient background.
 * Used for major statements, breaking emphasis, or story intros.
 */
import React from 'react';
import { AbsoluteFill, useCurrentFrame, interpolate } from 'remotion';
import type { SceneProps } from '../../types';
import {
  SceneContainer, HeadlineText, SubheadlineText,
  GlobalOverlays, AccentBar,
} from './shared';

export const L5_FullScreenHeadline: React.FC<SceneProps> = (props) => {
  const { headline, subheadline, colorPalette, headlineFont, subheadlineFont } = props;
  const frame = useCurrentFrame();

  // Animated background color pulse
  const glowOpacity = interpolate(
    Math.sin(frame * 0.05),
    [-1, 1],
    [0.05, 0.15],
  );

  return (
    <SceneContainer bg={colorPalette.primary}>
      {/* Animated background gradient */}
      <AbsoluteFill style={{
        background: `radial-gradient(ellipse at 30% 50%, ${colorPalette.secondary}${Math.round(glowOpacity * 255).toString(16).padStart(2, '0')} 0%, transparent 70%)`,
      }} />

      {/* Accent geometric elements */}
      <AbsoluteFill style={{ overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: -80, right: -80,
          width: 400, height: 400, borderRadius: '50%',
          border: `2px solid ${colorPalette.accent}22`,
        }} />
        <div style={{
          position: 'absolute', bottom: -60, left: -60,
          width: 300, height: 300, borderRadius: '50%',
          border: `2px solid ${colorPalette.accent}15`,
        }} />
      </AbsoluteFill>

      {/* Content — centered */}
      <AbsoluteFill style={{
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'flex-start',
        padding: '80px 160px 120px',
      }}>
        {/* Top accent line */}
        <AccentBar color={colorPalette.accent} style={{ marginBottom: 32, width: 80 }} />

        <HeadlineText
          text={headline}
          font={{
            ...headlineFont,
            fontSize: Math.min(headlineFont.fontSize * 1.3, 72),
            color: '#fff',
          }}
          delay={4}
          maxLines={4}
          layerKey="headline"
        />

        {subheadline && (
          <SubheadlineText
            text={subheadline}
            font={{ ...subheadlineFont, color: 'rgba(255,255,255,0.75)', fontSize: 28 }}
            delay={20}
            style={{ marginTop: 20 }}
            layerKey="subheadline"
          />
        )}

        {/* Bottom accent */}
        <div style={{
          marginTop: 32, height: 2, width: 48,
          background: colorPalette.accent, borderRadius: 2,
        }} />
      </AbsoluteFill>

      <GlobalOverlays props={props} />
    </SceneContainer>
  );
};
